/**
 * What a finished mission tells the roguelike layer, beyond the score (src/meta/types.ts).
 * Most of it is read off the final world; the rest are things that happened along the way,
 * kept in `MissionFacts` - plain data, so a suspended mission carries them with it.
 */

import type { SummaryExtras } from '../meta';
import { MEDAL_RULES } from '../meta/content/ranks';
import type { MissionSummary } from '../meta/types';
import { type DeathCamera, isPhotoId } from '../platform/photos/types';
import { isPrisoner } from '../sim/objectives';
import type { HurtCause, MissionDef, MissionResult, SimEvent, World } from '../sim/types';

export interface MissionFacts {
  wasDowned: boolean;
  downedByExplosion: boolean;
  /** Came down unhurt within `PRECISE_LANDING` metres of the planned landing point. */
  preciseLanding: boolean;
  /** Lucky charges he jumped with; fewer at the end means one was spent. */
  luckyAtStart: number;
  /** R6: a wound TAKEN, not one still open at the pickup - the Purple Heart is for being hit. */
  woundedInAction: boolean;
}

/** The Pathfinder Badge's own figure, read from the table that prints it (R6: this was a local 12). */
const PRECISE_LANDING = MEDAL_RULES.preciseLandingMetres;

const CAUSES: Record<HurtCause, string | null> = {
  none: null,
  gunfire: 'Killed by enemy fire',
  blast: 'Killed by an explosion',
  fire: 'Burned by a flamethrower',
  drowning: 'Drowned',
  ice: 'Went through the ice',
  wire: 'Died on the wire',
  landing: 'Killed on landing',
  vehicle: 'Run down by a vehicle',
};

export function newFacts(luckyAtStart: number): MissionFacts {
  return { wasDowned: false, downedByExplosion: false, preciseLanding: false, luckyAtStart, woundedInAction: false };
}

/** Call for every sim event, with the world as it stands after that tick. */
export function observe(facts: MissionFacts, event: SimEvent, world: World, def: MissionDef): void {
  if (event.type === 'rangerDowned') {
    facts.wasDowned = true;
    if (world.ranger.hurtBy === 'blast') facts.downedByExplosion = true;
  } else if (event.type === 'rangerHit' && event.wounded) {
    // R6: "wounded in action" is the wound, not what is left of it. A man shot twice who spends a
    // first-aid kit and walks out whole was still wounded in action, and used to get nothing.
    facts.woundedInAction = true;
  } else if (event.type === 'landed' && event.hazard === 'none') {
    const dx = world.ranger.x - def.player.x;
    const dz = world.ranger.z - def.player.z;
    facts.preciseLanding = dx * dx + dz * dz <= PRECISE_LANDING * PRECISE_LANDING;
  }
}

export function summarise(world: World, def: MissionDef, result: MissionResult, facts: MissionFacts): MissionSummary & SummaryExtras {
  const escorts = world.objective.captives.filter((c) => !isPrisoner(c.kind));
  const objective = world.objective;
  const summary: MissionSummary & SummaryExtras = {
    outcome: result.outcome,
    objectiveComplete: result.objectiveComplete,
    score: result.total,
    threat: def.threat,
    maxAlert: world.maxAlert,
    woundsAtEnd: world.ranger.wounds,
    wasDowned: facts.wasDowned,
    kills: world.ranger.kills,
    knifeKills: world.ranger.knifeKills,
    captivesSaved: escorts.filter((c) => c.state === 'boarded').length,
    // Completing mission 8 frees every captured ranger on the roster, as in 1987 (01 §2).
    freedPows: def.archetype === 'pow-camp' && result.objectiveComplete && result.outcome === 'extracted',
    luckyUsed: world.mods.luckyCharges < facts.luckyAtStart,
    captivesTotal: escorts.length,
    // Only missions that can succeed on part of their targets have "every unit" as a deed.
    allPartials: def.objective.minFraction < 1 && objective.total > 1 && objective.done >= objective.total,
    preciseLanding: facts.preciseLanding,
    downedByExplosion: facts.downedByExplosion,
    woundedInAction: facts.woundedInAction,
    secondsUsed: Math.max(0, Math.round(def.clockSeconds - result.secondsLeft)),
    tagsRecovered: world.objective.tagsRecovered,
    spanDeed: world.objective.spanDeed,
    // What he still carries comes home with him (unused smoke returns to the operation's stock).
    gearLeft: { ...world.ranger.gear },
  };
  if (result.outcome === 'kia') {
    const bledOut = facts.wasDowned && world.ranger.bleedTicks <= 0;
    const cause = bledOut ? 'Bled out before help could come' : CAUSES[world.ranger.hurtBy];
    if (cause) summary.cause = cause;
  }
  return summary;
}

/**
 * PHOTOS §4: killed in action, the picture of the moment he fell rides the summary to his memorial entry. No other
 * end takes one - the captured and the lost have no death to show - and without a camera (practice, training, the
 * Daily Drop) nothing is read. A camera that fails costs the picture, never the hand-over.
 */
export function photograph(summary: MissionSummary, camera: DeathCamera | undefined, pixels: () => Uint8Array): void {
  if (summary.outcome !== 'kia' || camera === undefined) return;
  try {
    const id = camera.take(pixels);
    if (isPhotoId(id)) summary.photo = id;
  } catch {
    // Nothing: the report goes on without a picture.
  }
}
